// Long-form, AEO-optimized content for IRS notice detail pages.
// Keep these answers concrete, citable, and aligned with IRS guidance.
// Each entry feeds: quick answer (TL;DR), why-you-got-it, response steps
// (HowTo schema), common mistakes, FAQs (FAQPage schema), and last reviewed.

export interface NoticeFAQ {
  question: string;
  answer: string;
}

export interface NoticeContent {
  /** 1–3 sentence direct answer for AI Overviews / featured snippets. */
  quickAnswer: string;
  /** 3–5 supporting bullets shown below the quick answer. */
  quickAnswerBullets?: string[];
  /** "Why did I get this?" — common triggers in plain language. */
  whyYouGotIt: string[];
  /** Ordered response steps — feeds visible numbered list + HowTo JSON-LD. */
  stepsToRespond: { title: string; description: string }[];
  /** Things people commonly get wrong. */
  commonMistakes: string[];
  /** People-Also-Ask style. Feeds FAQPage JSON-LD. */
  faqs: NoticeFAQ[];
  /** Optional related IRS forms by Form number (e.g. "Form 9465"). */
  relatedForms?: string[];
  /** ISO date for "Last reviewed" stamp. */
  lastReviewed: string;
}

export const noticeContent: Record<string, NoticeContent> = {
  cp14: {
    quickAnswer:
      "A CP14 is the IRS's first bill. It says you owe a balance — usually because the tax shown on your return wasn't paid in full by the due date. You have 21 days from the notice date to pay, set up a payment plan, or dispute the amount before penalties and interest grow and stronger collection notices begin.",
    quickAnswerBullets: [
      "First-stage collection notice — not yet a levy threat.",
      "Pay in full, request an installment agreement (Form 9465), or dispute the math.",
      "Interest compounds daily; the failure-to-pay penalty accrues monthly until paid.",
    ],
    whyYouGotIt: [
      "You filed a return but didn't pay the full balance due.",
      "An IRS adjustment (math error, missing payment, or amended return) created a new balance.",
      "Estimated-tax payments fell short of your final liability.",
    ],
    stepsToRespond: [
      {
        title: "Verify the amount",
        description:
          "Compare the CP14 figure to your filed return and any payments already made. Mistaken payments and unposted credits are the most common reasons CP14s look wrong.",
      },
      {
        title: "Pay in full if you can",
        description:
          "Paying inside the 21-day window stops the failure-to-pay penalty from accruing on the unpaid portion. Use IRS Direct Pay or EFTPS.",
      },
      {
        title: "If you can't pay, request a payment plan",
        description:
          "File Form 9465 (Installment Agreement Request) or apply online. Balances under $50,000 generally qualify for a streamlined agreement.",
      },
      {
        title: "If you disagree, call or respond in writing",
        description:
          "Use the phone number on the notice and have a copy of your return and payment confirmations ready. Document everything in writing.",
      },
      {
        title: "If hardship applies, ask about CNC status",
        description:
          "Currently Not Collectible status pauses collection if paying would create economic hardship. You'll need to substantiate income and expenses.",
      },
    ],
    commonMistakes: [
      "Ignoring the notice — escalation to CP501, CP503, CP504, then LT11 is automatic.",
      "Paying a balance that's actually wrong without first verifying credits and payments.",
      "Setting up a payment plan you can't afford — defaulting triggers a CP523.",
    ],
    faqs: [
      {
        question: "Is a CP14 the same as an audit?",
        answer:
          "No. A CP14 is a bill for taxes the IRS believes you owe based on your filed return. An audit (typically Letter 525 or Letter 2205) is an examination of the items on your return.",
      },
      {
        question: "How many days do I have to pay a CP14?",
        answer:
          "Twenty-one days from the notice date for balances under $100,000; ten business days for larger balances. After that, the failure-to-pay penalty rate doubles and the IRS begins issuing escalation notices.",
      },
      {
        question: "Can I ignore the CP14 if I can't pay?",
        answer:
          "No. Ignoring it does not pause penalties or interest, and the IRS will escalate to CP501, CP503, CP504, and eventually a final levy notice (LT11). Even a small monthly payment under an installment agreement stops escalation.",
      },
      {
        question: "Will the IRS levy my bank account from a CP14?",
        answer:
          "Not directly. The IRS must issue a Final Notice of Intent to Levy (CP504 then LT11/Letter 1058) and give you 30 days to request a Collection Due Process hearing before levying most assets.",
      },
    ],
    relatedForms: ["Form 9465", "Form 433-A"],
    lastReviewed: "2025-11-01",
  },
  cp501: {
    quickAnswer:
      "CP501 is the IRS's second reminder that you have an unpaid balance. It follows a CP14 you didn't resolve. You have about 21 days to pay, set up a payment plan, or respond — otherwise the IRS sends CP503, then CP504, and the path toward a levy begins.",
    whyYouGotIt: [
      "You received a CP14 and didn't pay, dispute, or set up an arrangement.",
      "A previous payment plan defaulted and the balance returned to active collection.",
      "An IRS adjustment increased your balance after your original return was filed.",
    ],
    stepsToRespond: [
      {
        title: "Don't wait for the next notice",
        description:
          "Each notice escalates collection authority. Address it before the IRS issues CP504, which threatens your state refund and other assets.",
      },
      {
        title: "Pay or apply for an installment agreement",
        description:
          "Most taxpayers owing under $50,000 qualify for a streamlined payment plan. Apply online or with Form 9465.",
      },
      {
        title: "If you can't pay, request CNC or an Offer in Compromise",
        description:
          "Currently Not Collectible status pauses collection during financial hardship. An Offer in Compromise (Form 656) settles the debt for less than owed when you qualify.",
      },
      {
        title: "If you disagree, call the number on the notice",
        description:
          "Have your return, payment confirmations, and any prior correspondence ready.",
      },
    ],
    commonMistakes: [
      "Treating the CP501 as a duplicate of the CP14 and ignoring it.",
      "Paying online without documenting it — keep your confirmation number.",
      "Waiting until CP504 to act, when the IRS already has authority to seize a state refund.",
    ],
    faqs: [
      {
        question: "Is a CP501 the same as a CP14?",
        answer:
          "No. CP14 is the first bill. CP501 is the second reminder that you didn't resolve the CP14. CP503 follows next, then CP504 (intent to levy your state refund), then LT11 (final intent to levy other assets).",
      },
      {
        question: "Does the IRS charge more for ignoring the CP501?",
        answer:
          "Yes. Interest compounds daily and the failure-to-pay penalty continues at 0.5% per month (up to 25% of the balance) until paid.",
      },
      {
        question: "Can I still set up a payment plan after a CP501?",
        answer:
          "Yes. You can request an installment agreement at any point before the IRS terminates your account or issues a levy. Acting earlier preserves the most options.",
      },
    ],
    relatedForms: ["Form 9465", "Form 656"],
    lastReviewed: "2025-11-01",
  },
  cp503: {
    quickAnswer:
      "CP503 is the IRS's third notice that your balance is unpaid. It's the last 'reminder' notice before the IRS issues CP504 — an intent to seize your state tax refund. Respond within 10 days to pay, set up an installment agreement, or contest the balance.",
    whyYouGotIt: [
      "You haven't responded to the earlier CP14 and CP501 notices.",
      "A prior payment plan defaulted without being reinstated.",
      "Penalties and interest have grown the balance since the original CP14.",
    ],
    stepsToRespond: [
      {
        title: "Treat this as urgent",
        description:
          "The next notice (CP504) authorizes the IRS to take your state refund and certain federal payments. Don't wait.",
      },
      {
        title: "Pay the full balance or request a payment plan",
        description:
          "Streamlined installment agreements remain available for balances under $50,000. Larger balances need Form 433-A or 433-F.",
      },
      {
        title: "If facing hardship, file for Currently Not Collectible status",
        description:
          "CNC pauses collection while you cannot pay basic living expenses. Be ready to substantiate income, expenses, and assets.",
      },
      {
        title: "Get representation if the balance is large or complex",
        description:
          "A CPA or Certified Tax Resolution Specialist with a Form 2848 power of attorney can communicate with the IRS on your behalf.",
      },
    ],
    commonMistakes: [
      "Assuming there's still time — CP504 typically follows within 5 weeks.",
      "Setting up a plan you can't actually afford, which leads to a CP523 default later.",
      "Failing to file unfiled returns first — the IRS will not finalize most resolution options until you are in filing compliance.",
    ],
    faqs: [
      {
        question: "What comes after a CP503?",
        answer:
          "CP504 (Notice of Intent to Seize State Refund), followed by LT11 or Letter 1058 (Final Notice of Intent to Levy — your last warning before bank and wage levies).",
      },
      {
        question: "How long after a CP503 does the IRS issue CP504?",
        answer:
          "Typically about five weeks, but it varies by case and IRS workload. Don't rely on the gap — act immediately.",
      },
      {
        question: "Can I still negotiate after a CP503?",
        answer:
          "Yes. Installment agreements, Offers in Compromise, and Currently Not Collectible status are all available. Earlier action means fewer accrued penalties and more leverage.",
      },
    ],
    relatedForms: ["Form 9465", "Form 433-A", "Form 2848"],
    lastReviewed: "2025-11-01",
  },
  cp504: {
    quickAnswer:
      "CP504 is a Notice of Intent to Levy your state tax refund. It also signals that the IRS may file a Notice of Federal Tax Lien and seize other federal payments. You have 30 days from the notice date to resolve the balance before the IRS escalates to a final levy notice (LT11/Letter 1058) covering bank accounts and wages.",
    whyYouGotIt: [
      "You did not respond to earlier CP14, CP501, and CP503 notices.",
      "Your IRS account remains in active collection status with a balance due.",
      "A prior installment agreement defaulted and was not reinstated.",
    ],
    stepsToRespond: [
      {
        title: "Stop the state refund seizure",
        description:
          "Pay the balance, request an installment agreement, or get representation immediately. CP504 specifically authorizes seizing your state refund.",
      },
      {
        title: "Request CDP rights when LT11 arrives",
        description:
          "CP504 does not yet grant Collection Due Process rights for most assets. The LT11/Letter 1058 that follows does — and you have only 30 days to request the hearing.",
      },
      {
        title: "File any unfiled returns",
        description:
          "The IRS will not finalize most resolution paths (installment agreement, OIC, CNC) while you are out of filing compliance.",
      },
      {
        title: "Submit Form 9465 or 433-A",
        description:
          "Form 9465 requests an installment agreement; Form 433-A documents financial hardship. A CPA can file these on your behalf with Form 2848.",
      },
    ],
    commonMistakes: [
      "Confusing CP504 with the final notice — LT11/Letter 1058 carries the CDP hearing rights, not CP504.",
      "Waiting until the IRS files a tax lien, which damages credit and complicates lending.",
      "Sending a partial payment without an active agreement — it doesn't stop collection escalation.",
    ],
    faqs: [
      {
        question: "Will the IRS take my paycheck after a CP504?",
        answer:
          "Not yet from CP504 alone. Wage garnishment requires the IRS to first issue a Final Notice of Intent to Levy (LT11 or Letter 1058) and wait 30 days. CP504 does authorize seizure of your state refund and certain federal payments.",
      },
      {
        question: "Does CP504 file a tax lien?",
        answer:
          "Not automatically. CP504 can lead to a Notice of Federal Tax Lien (NFTL), which the IRS files publicly to secure its position. Resolving the balance before the NFTL is filed protects your credit and lending options.",
      },
      {
        question: "How long do I have to respond to a CP504?",
        answer:
          "About 30 days. After that, the IRS typically issues LT11 or Letter 1058 — the final notice before bank and wage levies.",
      },
    ],
    relatedForms: ["Form 9465", "Form 433-A", "Form 2848", "Form 12153"],
    lastReviewed: "2025-11-01",
  },
  lt11: {
    quickAnswer:
      "LT11 (or Letter 1058) is the IRS's Final Notice of Intent to Levy and Notice of Your Right to a Hearing. You have 30 days from the notice date to request a Collection Due Process (CDP) hearing using Form 12153. If you do nothing, the IRS can legally levy your bank accounts, garnish wages, and seize property after the 30-day window closes.",
    quickAnswerBullets: [
      "30-day clock starts on the notice date — not the date you opened it.",
      "Filing Form 12153 (CDP hearing request) within 30 days pauses collection and preserves Tax Court rights.",
      "Missing the 30 days forfeits CDP rights — you can still request an Equivalent Hearing within one year, but you lose Tax Court appeal.",
    ],
    whyYouGotIt: [
      "Earlier notices (CP14 → CP501 → CP503 → CP504) went unresolved.",
      "A previous installment agreement defaulted (CP523) and was not reinstated.",
      "An Offer in Compromise or other resolution case was closed without an agreement.",
    ],
    stepsToRespond: [
      {
        title: "Mark the 30-day CDP deadline",
        description:
          "Calendar 30 calendar days from the notice date on the letter. This deadline is statutory — the IRS cannot extend it informally.",
      },
      {
        title: "File Form 12153 to request a CDP hearing",
        description:
          "Form 12153 (Request for a Collection Due Process or Equivalent Hearing) pauses levy action and routes your case to the IRS Office of Appeals. You can propose collection alternatives at the hearing.",
      },
      {
        title: "Get into filing compliance",
        description:
          "Unfiled returns block most resolution options. File any missing returns immediately, even if you can't pay.",
      },
      {
        title: "Choose a collection alternative",
        description:
          "Installment agreement (Form 9465), Currently Not Collectible status, or Offer in Compromise (Form 656). The hearing is your chance to negotiate.",
      },
      {
        title: "Authorize a representative",
        description:
          "A CPA, EA, or attorney with Form 2848 can attend the CDP hearing and negotiate on your behalf. Most taxpayers benefit from representation here.",
      },
    ],
    commonMistakes: [
      "Missing the 30-day window — Equivalent Hearings exist but waive Tax Court rights.",
      "Filing Form 12153 with no proposed alternative — the IRS will sustain the levy if you don't propose one.",
      "Believing the notice is a scam — IRS levy notices are sent by certified mail and look almost identical to scam letters. Verify by calling the number on IRS.gov, not the letter.",
    ],
    faqs: [
      {
        question: "What's the difference between LT11 and Letter 1058?",
        answer:
          "Both are Final Notices of Intent to Levy with CDP rights. LT11 is issued by the IRS Automated Collection System; Letter 1058 is issued by a Revenue Officer in person or by certified mail. Your rights and the 30-day deadline are identical.",
      },
      {
        question: "Can the IRS levy my bank account without an LT11?",
        answer:
          "Generally no. For most levies on bank accounts, wages, and property the IRS must first issue a final notice (LT11, Letter 1058, or CP90/CP297) and wait 30 days. State tax refunds and federal payments under the FPLP can be taken without LT11.",
      },
      {
        question: "What happens if I miss the 30 days on an LT11?",
        answer:
          "You forfeit your right to a Collection Due Process hearing — the version that pauses collection and preserves Tax Court appeal. You can still request an Equivalent Hearing within one year, but it does not pause collection and the decision cannot be appealed to Tax Court.",
      },
      {
        question: "Does filing Form 12153 stop the levy?",
        answer:
          "Yes — if filed within the 30-day window. The IRS suspends most levy action while Appeals reviews your case. The statute of limitations on collection is also suspended during the appeal.",
      },
    ],
    relatedForms: ["Form 12153", "Form 9465", "Form 433-A", "Form 656", "Form 2848"],
    lastReviewed: "2025-11-01",
  },
  cp2000: {
    quickAnswer:
      "CP2000 is not a bill and not an audit — it's a proposed change to your return because the IRS received income information (W-2s, 1099s) that doesn't match what you reported. You have 30 days to agree, partially agree, or disagree. Ignoring it converts the proposal into an assessment automatically through a Statutory Notice of Deficiency.",
    quickAnswerBullets: [
      "Match the IRS's figures to your records line by line — discrepancies are sometimes the IRS's mistake.",
      "Respond by the deadline even if you only partially agree; silence creates an assessment.",
      "If you agree, sign the response form and pay or set up a payment plan.",
    ],
    whyYouGotIt: [
      "A 1099 (NEC, MISC, INT, DIV, B, K) wasn't reported on your return.",
      "Stock-sale proceeds reported on Form 1099-B were included without the cost basis.",
      "A W-2 from a job you forgot about was reported by the employer.",
      "Retirement distributions, gambling winnings, or cryptocurrency transactions were under- or unreported.",
    ],
    stepsToRespond: [
      {
        title: "Read the entire notice",
        description:
          "Each line shows what the IRS received, what you reported, and the proposed change. Pages later in the notice show the recalculation.",
      },
      {
        title: "Match to your records",
        description:
          "Pull your bank statements, brokerage 1099s, and tax return. Common fixes: missing cost basis on stock sales, double-reported income, or income on the wrong return year.",
      },
      {
        title: "Check the response form",
        description:
          "If you agree, sign the response form and pay. If you disagree, check the disagree box and attach an explanation with supporting documents.",
      },
      {
        title: "If partial — agree to what's correct and dispute the rest",
        description:
          "Be specific: line item, IRS amount, your amount, why. Include 1099 copies, brokerage statements, or amended W-2s.",
      },
      {
        title: "Submit before the 30-day deadline",
        description:
          "Mail certified with return receipt or fax to the number on the notice. Keep proof of delivery.",
      },
    ],
    commonMistakes: [
      "Treating CP2000 as a final bill and paying without verifying.",
      "Filing an amended return (Form 1040-X) instead of responding directly — this complicates the case.",
      "Ignoring the notice — the IRS issues a Statutory Notice of Deficiency next, with only 90 days to petition Tax Court.",
    ],
    faqs: [
      {
        question: "Is a CP2000 an audit?",
        answer:
          "No. CP2000 is a document-matching notice. It compares third-party information returns (W-2s, 1099s) to your filed return. An audit (Letter 525, Letter 2205) is a broader examination of your return's items.",
      },
      {
        question: "What if I agree with the CP2000?",
        answer:
          "Sign and return the response form, then pay the balance. If you can't pay in full, you can request an installment agreement at the same time using Form 9465.",
      },
      {
        question: "How do I dispute a CP2000?",
        answer:
          "Check the 'disagree' box on the response form, attach a written explanation with supporting documents (1099s, brokerage statements, corrected W-2s), and mail or fax it back before the 30-day deadline.",
      },
      {
        question: "What happens if I ignore a CP2000?",
        answer:
          "The IRS issues a Statutory Notice of Deficiency (90-Day Letter). After 90 days the proposed change becomes a final assessment, and you must pay first and sue for a refund — or petition Tax Court within the 90 days to challenge before paying.",
      },
    ],
    relatedForms: ["Form 9465", "Form 1040-X"],
    lastReviewed: "2025-11-01",
  },
  letter525: {
    quickAnswer:
      "Letter 525 is the IRS's '30-Day Letter' — the general examination report you receive at the end of an audit. It explains proposed changes to your tax return and gives you 30 days to agree, request an Appeals conference, or file a written protest. Ignoring it leads to a Statutory Notice of Deficiency (90-Day Letter).",
    whyYouGotIt: [
      "You were audited (correspondence, office, or field audit) and the examiner finished the report.",
      "Your return was selected based on DIF score, mismatch flags, or related-party examinations.",
      "Schedule C deductions, large charitable contributions, or unreported income often trigger examinations.",
    ],
    stepsToRespond: [
      {
        title: "Compare the report to your records",
        description:
          "The examiner's Form 4549 shows each adjustment. Verify each line against your documentation.",
      },
      {
        title: "Decide: agree, appeal, or do nothing",
        description:
          "Sign and return if you agree. If you disagree, request an Appeals conference in writing. Doing nothing triggers a Statutory Notice of Deficiency.",
      },
      {
        title: "Request an Appeals conference",
        description:
          "For changes over $25,000 per period, file a formal written protest. Smaller cases can use a small case request.",
      },
      {
        title: "Bring representation",
        description:
          "A CPA or Certified Tax Resolution Specialist with Form 2848 can speak directly with the examiner and Appeals Officer.",
      },
      {
        title: "Preserve your Tax Court rights",
        description:
          "If Appeals doesn't resolve it, the next step is the Statutory Notice of Deficiency. You have 90 days from that notice to petition Tax Court without first paying.",
      },
    ],
    commonMistakes: [
      "Signing the agreement form without understanding the impact on future-year carryovers.",
      "Missing the 30-day deadline and losing the Appeals route.",
      "Going to Appeals without organized documentation — Appeals weighs hazards of litigation, not sympathy.",
    ],
    faqs: [
      {
        question: "Is Letter 525 the end of the audit?",
        answer:
          "It's the end of the examiner's work. You can still appeal to the IRS Office of Appeals, and after Appeals you can petition Tax Court if the IRS issues a Statutory Notice of Deficiency.",
      },
      {
        question: "How long do I have to respond to Letter 525?",
        answer:
          "Thirty days from the date on the letter. After that, the IRS typically issues a Statutory Notice of Deficiency, and your only remaining no-pay path is Tax Court within 90 days.",
      },
      {
        question: "Can I still go to Appeals after Letter 525?",
        answer:
          "Yes, if you respond within 30 days. Appeals offers an independent review separate from the examination function and resolves the majority of cases without litigation.",
      },
    ],
    relatedForms: ["Form 12203", "Form 2848"],
    lastReviewed: "2025-11-01",
  },
  cp508c: {
    quickAnswer:
      "CP508C means the IRS has certified your tax debt as 'seriously delinquent' to the State Department. The State Department will deny new passport applications and may revoke an existing passport. Certification typically applies when the total assessed federal tax debt (with penalties and interest) exceeds about $62,000 (2025 inflation-adjusted threshold) and the IRS has filed a lien or issued a levy.",
    whyYouGotIt: [
      "Your assessed federal tax debt exceeds the seriously delinquent threshold (adjusted annually for inflation).",
      "The IRS has filed a Notice of Federal Tax Lien or issued a levy.",
      "You're not currently in an installment agreement, OIC, CDP hearing, or innocent spouse case.",
    ],
    stepsToRespond: [
      {
        title: "Get into an active resolution",
        description:
          "An installment agreement, accepted OIC, CNC status, or pending CDP hearing pauses or reverses certification.",
      },
      {
        title: "Request decertification",
        description:
          "Once you're in a qualifying arrangement, the IRS reverses certification and notifies the State Department within 30 days.",
      },
      {
        title: "File any unfiled returns",
        description:
          "Most resolution options require filing compliance. File missing returns immediately.",
      },
      {
        title: "Consider expedited decertification if travel is urgent",
        description:
          "If you have international travel planned within 45 days and proof, the IRS may expedite the reversal.",
      },
    ],
    commonMistakes: [
      "Booking international travel before decertification is processed.",
      "Setting up an installment agreement and then defaulting, which can re-trigger certification.",
      "Ignoring the underlying tax debt — certification doesn't go away without resolution.",
    ],
    faqs: [
      {
        question: "What is the seriously delinquent tax debt threshold?",
        answer:
          "Adjusted annually for inflation. For 2025 the threshold is approximately $62,000 in assessed federal tax debt including penalties and interest. The IRS posts the current figure on irs.gov.",
      },
      {
        question: "Can I renew my passport while my debt is certified?",
        answer:
          "No. The State Department will deny new applications and renewals while certification is in place. You need to resolve the debt and receive a CP508R reversal notice first.",
      },
      {
        question: "How long does decertification take?",
        answer:
          "The IRS sends the reversal to the State Department within 30 days of qualifying. State Department processing adds additional time. Expedited processing is available for urgent travel with documentation.",
      },
    ],
    relatedForms: ["Form 9465", "Form 656", "Form 433-A"],
    lastReviewed: "2025-11-01",
  },
  "notice-deficiency": {
    quickAnswer:
      "The Statutory Notice of Deficiency (also called the '90-Day Letter' — typically CP3219A or CP3219N) is your legal ticket to U.S. Tax Court. You have exactly 90 days from the notice date (150 if addressed outside the U.S.) to file a Tax Court petition. Miss the deadline and the IRS assesses the tax — you must pay first and sue for a refund afterward.",
    quickAnswerBullets: [
      "The 90-day clock is statutory — the IRS and Tax Court cannot extend it.",
      "Filing a Tax Court petition is the only way to challenge the tax without paying first.",
      "Most cases settle in IRS Appeals before trial after the petition is filed.",
    ],
    whyYouGotIt: [
      "You didn't respond to a CP2000 underreporter notice.",
      "An audit (Letter 525) concluded without agreement and went through Appeals.",
      "The IRS is asserting a deficiency from a substitute-for-return assessment.",
    ],
    stepsToRespond: [
      {
        title: "Calendar the 90-day deadline today",
        description:
          "Use the notice date, not the date you opened the mail. The 90th calendar day is your filing deadline.",
      },
      {
        title: "Decide: pay, petition, or both",
        description:
          "Petitioning Tax Court preserves your no-pay rights. Paying the tax and suing for a refund in district court is the alternative.",
      },
      {
        title: "Prepare the Tax Court petition",
        description:
          "Use the Tax Court's online filing system or mail Form 2 (Petition). Small Tax Case procedure (under $50,000 per year) is simpler but has no appeal.",
      },
      {
        title: "Expect IRS Appeals to contact you",
        description:
          "Most petitioned cases are referred back to Appeals for settlement before trial.",
      },
      {
        title: "Hire a CPA, EA, or tax attorney admitted to Tax Court",
        description:
          "Tax Court representation is restricted to admitted practitioners. A CPA partnered with Tax Court counsel can manage the underlying tax dispute.",
      },
    ],
    commonMistakes: [
      "Treating the notice as just another bill and paying without preserving Tax Court rights.",
      "Filing the petition late — even one day past 90 forfeits the no-pay path.",
      "Choosing Small Tax Case procedure without realizing it waives the right to appeal.",
    ],
    faqs: [
      {
        question: "What is the 90-day letter from the IRS?",
        answer:
          "It's the Statutory Notice of Deficiency (usually CP3219A or CP3219N). It gives you 90 days (150 if abroad) to petition the U.S. Tax Court to dispute the proposed tax before it's assessed.",
      },
      {
        question: "Can the IRS extend the 90-day deadline?",
        answer:
          "No. The 90-day period is set by statute (IRC §6213). Neither the IRS nor the Tax Court can extend it. A petition mailed by the deadline counts as filed on time under the timely-mailing rule.",
      },
      {
        question: "What happens if I miss the 90 days?",
        answer:
          "The IRS assesses the proposed tax. Your remaining options are paying the balance and suing for a refund in U.S. District Court or the Court of Federal Claims, or addressing the assessed balance through collection alternatives like an installment agreement or Offer in Compromise.",
      },
    ],
    relatedForms: ["Form 2848"],
    lastReviewed: "2025-11-01",
  },
  cp523: {
    quickAnswer:
      "CP523 means the IRS is about to terminate your installment agreement, typically because you missed a payment, missed a new tax filing, or accrued new tax debt. You have 30 days to reinstate the agreement before the IRS terminates it and the full balance becomes due — at which point collection (including levies) can resume.",
    whyYouGotIt: [
      "You missed an installment agreement payment.",
      "A new return was filed late or with an unpaid balance.",
      "You incurred a new tax liability after the agreement was signed.",
      "The IRS reassessed your ability to pay and revised the agreement.",
    ],
    stepsToRespond: [
      {
        title: "Bring payments current immediately",
        description:
          "If a missed payment caused the default, paying within 30 days often reinstates the agreement.",
      },
      {
        title: "File any missing returns",
        description:
          "Filing compliance is a condition of every installment agreement. File first, then negotiate.",
      },
      {
        title: "Call the IRS or request reinstatement online",
        description:
          "Use the phone number on the notice. Reinstatement may carry a user fee.",
      },
      {
        title: "Renegotiate if you can't afford the existing plan",
        description:
          "File a new Form 433-A or 433-F to document hardship and request a lower payment, partial-pay agreement, or Currently Not Collectible status.",
      },
    ],
    commonMistakes: [
      "Letting the 30 days lapse — once terminated, restarting collection is harder and the full balance is due.",
      "Ignoring new-year tax liabilities — adding new debt voids most agreements.",
      "Sending payment without confirming reinstatement — the IRS still considers the agreement defaulted until they say otherwise.",
    ],
    faqs: [
      {
        question: "Can I reinstate a defaulted installment agreement?",
        answer:
          "Yes, often. Pay the missed amount, file missing returns, and call the IRS within the 30-day window on the CP523. A reinstatement user fee may apply unless you qualify for low-income relief.",
      },
      {
        question: "What happens if my installment agreement is terminated?",
        answer:
          "The full remaining balance becomes due immediately and the IRS can resume enforced collection — including liens, levies, and wage garnishments — after issuing required notices.",
      },
      {
        question: "Can I get a new installment agreement after a CP523 default?",
        answer:
          "Yes. You can negotiate a new agreement with different terms, especially if your financial situation has changed. Document hardship with Form 433-A or 433-F.",
      },
    ],
    relatedForms: ["Form 9465", "Form 433-A", "Form 433-F"],
    lastReviewed: "2025-11-01",
  },
};
